import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessages } from '../context/MessagesContext';
import {
  FiBarChart2,
  FiPackage,
  FiTruck,
  FiLayers,
  FiMap,
  FiNavigation,
  FiAlertTriangle,
  FiUsers,
  FiUserCheck,
  FiSettings,
  FiActivity,
  FiMail,
  FiMenu,
  FiX,
  FiBell,
} from 'react-icons/fi';
import FarmfleetLogoMark from '../components/FarmfleetLogoMark';
import './Dashboard.css';

function Dashboard() {
  const { admin, logout } = useAuth();
  const { unreadCount } = useMessages();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const currentPath = pathname.replace(/\/+$/, '') || '/';
  /* Function className so React Router does not auto-append `active` on a string.
     Only `.is-current` is styled — hover / aria-current must not look selected. */
  const navClass = (to) => () => (currentPath === to ? 'sidebar-link is-current' : 'sidebar-link');

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className={`dashboard ${sidebarOpen ? 'dashboard-drawer-open' : ''}`}>
      <div className={`dashboard-mobile-header ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={sidebarOpen}
        >
          {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
        <span className="dashboard-mobile-brand">FARMFLEET</span>
        <button
          type="button"
          className="dashboard-bell dashboard-bell-mobile"
          onClick={() => navigate('/dashboard/messages')}
          aria-label={`Messages${unreadCount ? `, ${unreadCount} unread` : ''}`}
        >
          <FiBell size={22} />
          {unreadCount > 0 && <span className="dashboard-bell-badge">{badgeLabel}</span>}
        </button>
      </div>
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <FarmfleetLogoMark />
          </div>
          <span className="sidebar-brand">FARMFLEET</span>
          <button
            type="button"
            className="sidebar-close-mobile"
            onClick={closeSidebar}
            aria-label="Close menu"
          >
            <FiX size={22} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <NavLink to="/dashboard" end className={navClass('/dashboard')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiBarChart2 size={20} /></span>
            Overview
          </NavLink>
          <NavLink to="/dashboard/orders" end className={navClass('/dashboard/orders')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiPackage size={20} /></span>
            Orders
          </NavLink>
          <NavLink to="/dashboard/trips" end className={navClass('/dashboard/trips')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiTruck size={20} /></span>
            Trip Management
          </NavLink>
          <NavLink to="/dashboard/dispatch" end className={navClass('/dashboard/dispatch')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiLayers size={20} /></span>
            Dispatch Board
          </NavLink>
          <NavLink to="/dashboard/routes" end className={navClass('/dashboard/routes')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiMap size={20} /></span>
            Routes
          </NavLink>
          <NavLink to="/dashboard/fleet" end className={navClass('/dashboard/fleet')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiNavigation size={20} /></span>
            Fleet
          </NavLink>
          <NavLink to="/dashboard/roads" end className={navClass('/dashboard/roads')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiAlertTriangle size={20} /></span>
            Road conditions
          </NavLink>
          <NavLink to="/dashboard/farmers" end className={navClass('/dashboard/farmers')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiUsers size={20} /></span>
            Farmers
          </NavLink>
          <NavLink to="/dashboard/drivers" end className={navClass('/dashboard/drivers')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiUserCheck size={20} /></span>
            Drivers
          </NavLink>
          <NavLink to="/dashboard/messages" end className={navClass('/dashboard/messages')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiMail size={20} /></span>
            Contact inbox
            {unreadCount > 0 && <span className="sidebar-nav-badge">{badgeLabel}</span>}
          </NavLink>
          <NavLink to="/dashboard/activity-log" end className={navClass('/dashboard/activity-log')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiActivity size={20} /></span>
            Activity Log
          </NavLink>
          <NavLink to="/dashboard/settings" end className={navClass('/dashboard/settings')} onClick={closeSidebar}>
            <span className="sidebar-link-icon"><FiSettings size={20} /></span>
            Settings
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">{admin?.username}</div>
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>
      <div className="dashboard-content-column">
        <header className="dashboard-topbar">
          <span className="dashboard-topbar-title">Dispatch console</span>
          <button
            type="button"
            className="dashboard-bell"
            onClick={() => navigate('/dashboard/messages')}
            aria-label={`Messages${unreadCount ? `, ${unreadCount} unread` : ''}`}
          >
            <FiBell size={22} />
            {unreadCount > 0 && <span className="dashboard-bell-badge">{badgeLabel}</span>}
          </button>
        </header>
        <main className="dashboard-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
